// BLISS bKash "Order Now" - the behaviour behind the button.
//
// This lives in a file of its own, and that is the whole point.
//
// It used to be an inline <script> inside the button snippet, which works on
// the cart page and fails completely in the cart drawer. The drawer is built
// like this (snippets/cart-drawer.liquid):
//
//     {%- if cart.empty? -%}  ...empty drawer...
//     {%- else -%}            ...{% render 'cart-summary' %}...
//
// so when a page loads with an empty cart, the summary - and with it the
// button and its inline script - is not in the page at all. Adding the first
// product makes the theme fetch the drawer's HTML and morph it in, and a
// <script> arriving that way is never executed by the browser. The shopper got
// a button with nothing behind it: no handler, no message, no console line.
// Reloading appeared to fix it only because by then the cart was no longer
// empty, so the server rendered the script after all.
//
// As a separate asset it is loaded once per page by a tag that is always
// rendered, and the button can arrive whenever it likes afterwards.

(function () {
  var API_BASE = 'https://bliss-bkash-pay.vercel.app';

  // Must match ATTRIBUTE_KEY in pages/api/webhook/shopify-order.js. Kept
  // human-readable on purpose: it shows on the order in Shopify admin, where
  // it tells staff which bKash payment the order belongs to.
  var ORDER_ATTRIBUTE = 'bKash Order';

  // How long one attempt may hold the lock. Long enough for a slow phone on
  // a bad connection, short enough that a request which died without ever
  // resolving - a tab suspended mid-flight, a network that vanished - cannot
  // leave the button dead for the rest of the visit.
  var BUSY_MS = 25000;

  // A shop counter has no developer console, and most of this shop's testing
  // happens on a phone where there is no way to open one at all. So the page
  // can be asked to narrate itself on screen instead: add ?blissdebug=1 to
  // the cart URL. Nothing shows without it, so a customer never sees this.
  var DEBUG = (location.search || '').indexOf('blissdebug=1') !== -1;
  var panel = null;

  function show(msg) {
    if (!DEBUG) return;
    if (!panel) {
      panel = document.createElement('div');
      panel.style.cssText = 'position:fixed;left:8px;right:8px;bottom:8px;z-index:2147483647;'
        + 'max-height:45vh;overflow:auto;background:#1F1B2D;color:#fff;font:12px/1.5 monospace;'
        + 'padding:10px 12px;border-radius:8px;white-space:pre-wrap;word-break:break-word';
      (document.body || document.documentElement).appendChild(panel);
    }
    var line = document.createElement('div');
    line.textContent = msg;
    panel.appendChild(line);
    panel.scrollTop = panel.scrollHeight;
  }

  function log() {
    var args = ['[bliss]'];
    for (var i = 0; i < arguments.length; i++) args.push(arguments[i]);
    if (window.console && console.log) console.log.apply(console, args);
    show(args.join(' '));
  }

  // One in-flight order at a time, no matter how many buttons are on screen
  // or how many times the shopper presses. Stamped rather than flagged, so a
  // lock that was never released expires instead of lasting for ever.
  if (window.__blissBkashBusyAt === undefined) window.__blissBkashBusyAt = 0;

  function busy() {
    if (!window.__blissBkashBusyAt) return false;
    if (Date.now() - window.__blissBkashBusyAt < BUSY_MS) return true;
    log('previous attempt never finished; letting this one through');
    window.__blissBkashBusyAt = 0;
    return false;
  }

  // The note field that is actually on the page. A re-render can leave an
  // orphaned copy behind, and its value is always empty - posting that would
  // erase what the shopper wrote.
  function liveNoteField() {
    var fields = document.querySelectorAll('[name="note"]');
    var connected = null;
    for (var i = 0; i < fields.length; i++) {
      var f = fields[i];
      if (!f.isConnected) continue;
      if (f.offsetWidth || f.offsetHeight) return f;
      if (!connected) connected = f;
    }
    return connected;
  }

  function buttons() {
    return document.querySelectorAll('.bliss-bkash-checkout-btn');
  }

  /** Puts every copy of the button back the way it was. */
  function release(originalText) {
    window.__blissBkashBusyAt = 0;
    var all = buttons();
    for (var i = 0; i < all.length; i++) {
      all[i].disabled = false;
      all[i].removeAttribute('data-bliss-working');
      var l = all[i].querySelector('.button-text');
      if (l && originalText) l.textContent = originalText;
    }
  }

  function start(btn) {
    if (busy()) { log('already working; ignoring this press'); return; }
    window.__blissBkashBusyAt = Date.now();

    var label = btn.querySelector('.button-text');
    var originalText = label ? label.textContent : 'Order Now';
    btn.disabled = true;
    // Marks this as OUR doing. The click handler refuses a button only when
    // this attribute is on it, so a button the theme happens to have
    // disabled for its own reasons can still be pressed.
    btn.setAttribute('data-bliss-working', '1');
    if (label) label.textContent = 'Please wait...';

    // Read before anything else: the shopper may have typed a note and never
    // left the field, in which case the cart on the server does not have it.
    var field = liveNoteField();
    var typedNote = field ? (field.value || '') : null;

    log('starting; note field found:', field ? 'yes' : 'no');

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items || !cart.items.length) {
          var empty = new Error('empty cart');
          empty.shopperMessage = 'আপনার কার্ট খালি। আগে পণ্য যোগ করুন।';
          throw empty;
        }
        log('cart read:', cart.items.length, 'lines');

        // Send the SAME figures the cart page is displaying, so the two
        // screens cannot disagree.
        //
        // final_price / final_line_price are what the cart shows per line:
        // they include line-level discounts but NOT cart-level ones. The
        // cart-level ones are listed separately underneath, exactly as the
        // cart page lists them - so distributing them into the line prices
        // here would double-count them and make the items add up to less
        // than the stated subtotal.
        var lineItems = cart.items.map(function (i) {
          var qty = i.quantity || 1;
          var unitFinal = (i.final_price != null ? i.final_price : i.price) / 100;
          var unitOriginal = (i.original_price != null ? i.original_price : i.price) / 100;
          return {
            title: i.product_title + (i.variant_title ? ' - ' + i.variant_title : ''),
            product_title: i.product_title,
            variant_title: i.variant_title || null,
            quantity: qty,
            price: unitFinal,
            // Struck through only when a discount really did lower this line.
            original_price: unitOriginal > unitFinal ? unitOriginal : null,
            line_total: (i.final_line_price != null ? i.final_line_price : (i.final_price || 0) * qty) / 100,
            variant_id: i.variant_id,
            image: i.image || i.featured_image || null,
            url: i.url || null
          };
        });

        // Every discount the cart is showing, coded or automatic - including
        // ones worth 0, because the cart page lists those too.
        var discounts = [];
        (cart.cart_level_discount_applications || []).forEach(function (d) {
          discounts.push({
            title: d.title || d.description || 'Discount',
            code: d.type === 'discount_code' ? d.title : null,
            amount: (d.total_allocated_amount || 0) / 100
          });
        });
        cart.items.forEach(function (i) {
          (i.line_level_discount_allocations || []).forEach(function (a) {
            if (!a.amount) return;
            var app = a.discount_application || {};
            discounts.push({
              title: app.title || 'Discount',
              code: app.type === 'discount_code' ? app.title : null,
              amount: a.amount / 100
            });
          });
        });

        // The codes the shopper actually typed, straight from the cart.
        var codes = (cart.discount_codes || [])
          .filter(function (d) { return d.applicable !== false; })
          .map(function (d) { return d.code; });

        // What the shopper can see on screen wins over what the server has
        // stored, because the field may not have been saved yet.
        var note = typedNote != null ? typedNote : (cart.note || '');

        return fetch(API_BASE + '/api/intent/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line_items: lineItems,
            // Shopify's own id for this cart. It is what lets the service
            // retire the row left behind the last time this cart pressed the
            // button - without it, one order shows up in the shop's panel
            // two or three times over, in two or three different stages.
            cart_token: cart.token || null,
            // total_price is what the shopper owes for the goods after every
            // discount has been applied.
            cart_total: cart.total_price / 100,
            // items_subtotal_price is the cart's own "Subtotal", and always
            // equals the sum of the line totals above.
            items_subtotal: (cart.items_subtotal_price != null
              ? cart.items_subtotal_price
              : cart.total_price) / 100,
            cart_note: note,
            cart_discounts: discounts,
            discount_codes: codes,
            currency: cart.currency
          })
        }).then(function (r) { return r.json(); })
          .then(function (data) {
            if (!data || !data.pay_url) {
              throw new Error(data && data.error ? data.error : 'no pay_url');
            }
            log('service answered:', data.intent_id);
            return { data: data, cart: cart, note: note };
          });
      })
      .then(function (ctx) {
        // The one and only write to the cart, and the last thing that
        // happens here. The attribute has to be saved - without it the order
        // the shopper places after paying cannot be matched to their money -
        // and the note rides along in the same call so the theme is asked to
        // re-render at most once, with nothing left to interrupt.
        var body = {};
        body.attributes = {};
        body.attributes[ORDER_ATTRIBUTE] = ctx.data.intent_id;
        if (ctx.note !== (ctx.cart.note || '')) body.note = ctx.note;

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }).then(function (r) {
          if (!r.ok) throw new Error('could not stamp the cart (' + r.status + ')');
          log('stamped', ctx.data.intent_id, '- going to', ctx.data.pay_url);
          window.location.href = ctx.data.pay_url;
        });
      })
      .catch(function (err) {
        log('order failed:', (err && err.message) || err);
        if (!DEBUG) alert((err && err.shopperMessage) || 'দুঃখিত, একটি সমস্যা হয়েছে। আবার চেষ্টা করুন।');
        release(originalText);
      });
  }

  function describe(el) {
    if (!el || !el.tagName) return String(el);
    return el.tagName.toLowerCase()
      + (el.id ? '#' + el.id : '')
      + (el.className && el.className.baseVal === undefined && el.className
          ? '.' + String(el.className).trim().split(/\s+/).join('.') : '');
  }

  function onClick(e) {
    var btn = e.target && e.target.closest && e.target.closest('.bliss-bkash-checkout-btn');
    if (!btn) {
      // Worth saying out loud while debugging: if the shopper is pressing a
      // button that is not ours, that is the whole answer, and no amount of
      // work inside start() would ever have shown up.
      if (DEBUG) show('click on ' + describe(e.target) + ' - NOT our button');
      return;
    }
    if (DEBUG) show('click on our button, ok');
    // Only our own "working" state stops a press. A button the theme has
    // disabled for its own reasons used to fail here in complete silence.
    if (btn.getAttribute('data-bliss-working') === '1') {
      log('press ignored: this button is already working');
      return;
    }
    e.preventDefault();
    // The theme has its own handlers on .cart__checkout-button. Ours runs
    // first and stops there, so nothing else tries to submit the cart form
    // underneath us.
    e.stopPropagation();
    start(btn);
  }

  // Wired twice on purpose. The capture listener catches the click before
  // any theme handler can swallow it; the per-button listener is what keeps
  // working if the document listener is ever lost with an older copy of the
  // page. Both are idempotent, and the busy stamp stops one click being
  // treated as two.
  function wire() {
    var btns = buttons();
    for (var i = 0; i < btns.length; i++) {
      if (btns[i].__blissWired) continue;
      btns[i].__blissWired = true;
      btns[i].addEventListener('click', onClick);
    }
  }

  if (!window.__blissBkashDocWired) {
    window.__blissBkashDocWired = true;
    document.addEventListener('click', onClick, true);

    // Coming back with the browser's Back button restores this page exactly
    // as it was left - including a button still reading "Please wait..." and
    // still disabled from the press that took the shopper away. Without this
    // the shopper returns to a dead button and has to reload.
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted && !window.__blissBkashBusyAt) return;
      log('restored from cache; freeing the button');
      release('Order Now');
    });
  }

  wire();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wire);
  }

  if (DEBUG) {
    var report = function () {
      show('--- bliss debug ---');
      show('script ran, readyState=' + document.readyState);
      show('our buttons on page: ' + buttons().length);
      show('note fields: ' + document.querySelectorAll('[name=\"note\"]').length);
      show('other checkout buttons: '
        + document.querySelectorAll('[name=\"checkout\"], .cart__checkout-button').length);
      show('press Order Now now');
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', report);
    } else {
      report();
    }
  }

  // The cart section is re-rendered on every quantity change, discount code
  // and note edit. Each render brings a new button that nothing is listening
  // to yet.
  if (!window.__blissBkashObserver && window.MutationObserver) {
    window.__blissBkashObserver = new MutationObserver(wire);
    window.__blissBkashObserver.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
